import { useMemo } from 'react'

import { type QuoteResult } from 'components/quoting/types'
import { calculateMonthlyEmployeeOutOfPocketContribution, calculateMonthlyPriceForEmployeeAndDependentsForBenchmarkedPlan } from 'utils/quote_calculations'

export type QuoteResultCosts = {
  totalMonthlyCost: number
  totalMonthlyEmployeeContribution: number
  totalMonthlyEmployerCost: number
  currentPlanTotalEmployeeMedicalContribution: number
  currentPlanTotalEmployerMedicalContribution: number
}

const useQuoteResultCosts = (
  quoteResults: QuoteResult[] | undefined,
  employerEmployeeContribution: number,
  employerSpouseContribution: number,
  employerChildContribution: number
): QuoteResultCosts => {
  const totalMonthlyCost = useMemo(() => (
    quoteResults?.reduce((acc, curr) => acc + calculateMonthlyPriceForEmployeeAndDependentsForBenchmarkedPlan(curr), 0) ?? 0
  ), [quoteResults])

  const totalMonthlyEmployeeContribution = useMemo(() => (
    quoteResults?.reduce((acc, curr) => (
      acc + calculateMonthlyEmployeeOutOfPocketContribution(curr, { employerEmployeeContribution, employerSpouseContribution, employerChildContribution })
    ), 0) ?? 0
  ), [quoteResults, employerEmployeeContribution, employerSpouseContribution, employerChildContribution])

  const totalMonthlyEmployerCost = useMemo(() => (
    totalMonthlyCost - totalMonthlyEmployeeContribution
  ), [totalMonthlyCost, totalMonthlyEmployeeContribution])

  const currentPlanTotalEmployeeMedicalContribution = useMemo(() => {
    if (!quoteResults) return 0
    return quoteResults.reduce((acc, curr) => (
      acc + Number(curr.employee_medical_contribution) || 0
    ), 0)
  }, [quoteResults])

  const currentPlanTotalEmployerMedicalContribution = useMemo(() => {
    if (!quoteResults) return 0
    return quoteResults.reduce((acc, curr) => (
      acc + Number(curr.employer_medical_contribution) || 0
    ), 0)
  }, [quoteResults])

  return {
    totalMonthlyCost,
    totalMonthlyEmployeeContribution,
    totalMonthlyEmployerCost,
    currentPlanTotalEmployeeMedicalContribution,
    currentPlanTotalEmployerMedicalContribution,
  }
}

export default useQuoteResultCosts
